import { useEffect, useState } from "react";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";
import {
  Users,
  BadgeCheck,
  Crown,
  Ban,
  Heart,
  MessageSquare,
  Flag,
  UserPlus,
  TrendingUp,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/Layout";
import { Spinner } from "@/components/shared";

const KPIS = [
  { key: "users", label: "Total Users", icon: Users, tone: "text-sky-300" },
  { key: "verified", label: "Verified", icon: BadgeCheck, tone: "text-emerald-300" },
  { key: "premium", label: "Premium", icon: Crown, tone: "text-amber-300" },
  { key: "banned", label: "Banned", icon: Ban, tone: "text-red-300" },
  { key: "matches", label: "Matches", icon: Heart, tone: "text-primary-foreground" },
  { key: "messages", label: "Messages", icon: MessageSquare, tone: "text-sky-300" },
  { key: "open_reports", label: "Open Reports", icon: Flag, tone: "text-red-300" },
  { key: "signups_today", label: "Signups Today", icon: UserPlus, tone: "text-emerald-300" },
];

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg bg-popover ring-1 ring-border px-3 py-2 text-xs shadow-xl">
      <div className="font-semibold mb-1">{label}</div>
      {payload.map((p) => (
        <div key={p.dataKey} className="flex items-center gap-2">
          <span className="h-2 w-2 rounded-full" style={{ background: p.color }} />
          <span className="text-muted-foreground capitalize">{p.name}:</span>
          <span className="font-semibold">{p.value}</span>
        </div>
      ))}
    </div>
  );
}

function TrendChart({ title, data, dataKey, color, gradId }) {
  return (
    <div className="rounded-2xl bg-card ring-1 ring-border/70 p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-display text-lg">{title}</h3>
        <TrendingUp className="h-4 w-4 text-muted-foreground" />
      </div>
      <div className="h-56">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data} margin={{ top: 4, right: 6, left: -18, bottom: 0 }}>
            <defs>
              <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={color} stopOpacity={0.4} />
                <stop offset="100%" stopColor={color} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(189 40% 26%)" vertical={false} />
            <XAxis
              dataKey="label"
              tick={{ fontSize: 11, fill: "hsl(189 12% 66%)" }}
              tickLine={false}
              axisLine={false}
              interval="preserveStartEnd"
            />
            <YAxis
              tick={{ fontSize: 11, fill: "hsl(189 12% 66%)" }}
              tickLine={false}
              axisLine={false}
              allowDecimals={false}
            />
            <Tooltip content={<ChartTooltip />} />
            <Area
              type="monotone"
              dataKey={dataKey}
              name={dataKey}
              stroke={color}
              strokeWidth={2.5}
              fill={`url(#${gradId})`}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [trends, setTrends] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [s, t] = await Promise.all([api.stats(), api.trends(14)]);
        setStats(s);
        setTrends(t);
      } catch (e) {
        toast.error(e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return <Spinner />;

  const chartData =
    trends?.labels?.map((label, i) => ({
      label,
      signups: trends.signups?.[i] ?? 0,
      dau: trends.dau?.[i] ?? 0,
    })) || [];

  return (
    <div>
      <PageHeader
        title="Dashboard"
        subtitle="Live overview of the Amoreska community."
      />

      {/* KPI cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="kpi-grid">
        {KPIS.map((k) => (
          <div
            key={k.key}
            className="rounded-2xl bg-card ring-1 ring-border/70 p-5 hover:ring-primary/40 transition-all duration-200"
            data-testid={`kpi-${k.key}`}
          >
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">{k.label}</span>
              <k.icon className={`h-[18px] w-[18px] ${k.tone}`} />
            </div>
            <div className="font-display text-3xl mt-3">
              {stats?.[k.key] ?? 0}
            </div>
          </div>
        ))}
      </div>

      {/* Match-rate strip */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4">
        <div className="rounded-2xl bg-gradient-to-br from-primary to-[hsl(8_55%_44%)] p-5 text-primary-foreground">
          <div className="text-sm/none opacity-80">Match Rate</div>
          <div className="font-display text-4xl mt-2">{trends?.match_rate ?? 0}%</div>
        </div>
        <div className="rounded-2xl bg-card ring-1 ring-border/70 p-5">
          <div className="text-sm text-muted-foreground">Total Likes</div>
          <div className="font-display text-4xl mt-2">{trends?.total_likes ?? 0}</div>
        </div>
        <div className="rounded-2xl bg-card ring-1 ring-border/70 p-5">
          <div className="text-sm text-muted-foreground">Total Matches</div>
          <div className="font-display text-4xl mt-2">{trends?.total_matches ?? 0}</div>
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
        <TrendChart
          title="Signups (14 days)"
          data={chartData}
          dataKey="signups"
          color="#C45A49"
          gradId="gradSignups"
        />
        <TrendChart
          title="Daily Active Users"
          data={chartData}
          dataKey="dau"
          color="#5FC7B8"
          gradId="gradDau"
        />
      </div>
    </div>
  );
}
